import React, { useState } from 'react';
import { Menu, X, Calendar, MapPin, Ticket, Users, ChevronDown, Settings } from 'lucide-react';
import { trackAffiliateClick } from '../utils/analytics';
import { useTheme } from '../contexts/ThemeContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showTeamMenu, setShowTeamMenu] = useState(false);
  const { selectedTeam, teamColors, clearTeam } = useTheme();

  const affiliateLinks = [
    { name: 'StubHub', url: 'https://stubhub.prf.hn/l/W4EJzJX', icon: Ticket, color: 'bg-blue-600' },
    { name: 'Ticketmaster', url: 'https://ticketmaster.evyy.net/c/4976406/264167/4272?u=https%3A%2F%2Fwww.ticketmaster.com', icon: Calendar, color: 'bg-blue-700' },
    { name: 'Expedia', url: 'https://expedia.com/affiliate/QloXX5p', icon: MapPin, color: 'bg-orange-600' },
    { name: 'VRBO', url: 'https://vrbo.com/affiliate/oWiAOm8', icon: Users, color: 'bg-red-600' },
  ];

  const isNeutralPartner = (name) => name === 'StubHub' || name === 'Ticketmaster';

  return (
    <header 
      className="bg-white/90 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50"
      style={{
        borderBottomColor: selectedTeam ? teamColors.primary + '30' : undefined
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <a 
              href="/" 
              className="text-xl md:text-2xl font-bold bg-clip-text text-transparent"
              style={{
                backgroundImage: selectedTeam 
                  ? `linear-gradient(to right, ${teamColors.primary}, ${teamColors.secondary})`
                  : 'linear-gradient(to right, #0ea5e9, #f97316)'
              }}
            >
              40 Bags Entertainment Inc.
            </a>
          </div>

          {/* Team Indicator - Desktop */}
          {selectedTeam && (
            <div className="hidden md:flex items-center relative">
              <button
                onClick={() => setShowTeamMenu(!showTeamMenu)}
                className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                style={{ borderColor: teamColors.primary + '30' }}
              >
                <div 
                  className="w-6 h-6 rounded-full border-2 border-white shadow-sm"
                  style={{ backgroundColor: teamColors.primary }}
                ></div>
                <span className="text-sm font-medium text-gray-700">{selectedTeam.name}</span>
                <ChevronDown size={16} className="text-gray-500" />
              </button>

              {/* Team Dropdown */}
              {showTeamMenu && (
                <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <div className="flex items-center space-x-3">
                      <div 
                        className="w-8 h-8 rounded-full"
                        style={{ 
                          background: `linear-gradient(135deg, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)` 
                        }}
                      ></div>
                      <div>
                        <p className="font-semibold text-gray-900">{selectedTeam.name}</p>
                        <p className="text-sm text-gray-500">Your selected team</p>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      clearTeam();
                      setShowTeamMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                  >
                    <Settings size={16} />
                    <span>Change Team</span>
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex space-x-6">
            <a href="/" className="text-gray-700 hover:text-primary-600 font-medium transition-colors text-sm">
              Home
            </a>
            <a href="/tailgate-central" className="text-gray-700 hover:text-primary-600 font-medium transition-colors text-sm">
              Tailgate Central
            </a>
            {/* First Snap hidden - not ready for production
            <a href="/first-snap" className="text-gray-700 hover:text-primary-600 font-medium transition-colors text-sm">
              First Snap
            </a>
            */}
            <a href="/playlists" className="text-gray-700 hover:text-primary-600 font-medium transition-colors text-sm">
              Playlists
            </a>
            <a href="/merch" className="text-gray-700 hover:text-primary-600 font-medium transition-colors text-sm">
              Merch
            </a>
            {/* Blog hidden - not ready for public viewing
            <a href="/blog" className="text-gray-700 hover:text-primary-600 font-medium transition-colors text-sm">
              Blog
            </a>
            */}
            <a href="/about" className="text-gray-700 hover:text-primary-600 font-medium transition-colors text-sm">
              About
            </a>
          </nav>

          {/* Removed header Spotify mini-player in favor of global sticky player */}

          {/* Affiliate Quick Links - Desktop Only for XL screens */}
          <div className="hidden xl:flex items-center space-x-2">
            {affiliateLinks.slice(0, 2).map((link) => {
              const IconComponent = link.icon;
              const neutral = isNeutralPartner(link.name);
              return (
                <a
                  key={link.name}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackAffiliateClick(link.name, link.url, { location: 'Header-XL' })}
                  className={`${neutral ? 'bg-gray-100 text-gray-900 border border-gray-300' : `${link.color} text-white`} hover:opacity-90 px-2 py-1 rounded-lg text-xs font-medium transition-all duration-200 transform hover:scale-105 flex items-center space-x-1`}
                >
                  <IconComponent size={14} />
                  <span className="hidden 2xl:inline">{link.name}</span>
                </a>
              );
            })}
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-700 hover:text-primary-600 transition-colors p-2"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 space-y-1 border-t border-gray-200 bg-white/95 backdrop-blur-md">
            {/* Team Indicator - Mobile */}
            {selectedTeam && (
              <div className="px-4 py-3 border-b border-gray-200 mb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div 
                      className="w-6 h-6 rounded-full"
                      style={{ backgroundColor: teamColors.primary }}
                    ></div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{selectedTeam.name}</p>
                      <p className="text-xs text-gray-500">Your selected team</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      clearTeam();
                      setIsMenuOpen(false);
                    }}
                    className="text-xs text-gray-500 hover:text-gray-700"
                  >
                    Change
                  </button>
                </div>
              </div>
            )}

            <a href="/" className="block text-gray-700 hover:text-primary-600 font-medium py-3 px-4 rounded-lg hover:bg-gray-50 transition-colors">
              Home
            </a>
            <a href="/tailgate-central" className="block text-gray-700 hover:text-primary-600 font-medium py-3 px-4 rounded-lg hover:bg-gray-50 transition-colors">
              Tailgate Central
            </a>
            {/* First Snap hidden - not ready for production
            <a href="/first-snap" className="block text-gray-700 hover:text-primary-600 font-medium py-3 px-4 rounded-lg hover:bg-gray-50 transition-colors">
              First Snap
            </a>
            */}
            <a href="/playlists" className="block text-gray-700 hover:text-primary-600 font-medium py-3 px-4 rounded-lg hover:bg-gray-50 transition-colors">
              Playlists
            </a>
            <a href="/merch" className="block text-gray-700 hover:text-primary-600 font-medium py-3 px-4 rounded-lg hover:bg-gray-50 transition-colors">
              Merch
            </a>
            {/* Blog hidden - not ready for public viewing
            <a href="/blog" className="block text-gray-700 hover:text-primary-600 font-medium py-3 px-4 rounded-lg hover:bg-gray-50 transition-colors">
              Blog
            </a>
            */}
            <a href="/about" className="block text-gray-700 hover:text-primary-600 font-medium py-3 px-4 rounded-lg hover:bg-gray-50 transition-colors">
              About
            </a>
            
            {/* Mobile Affiliate Links */}
            <div className="pt-4 border-t border-gray-200 mt-4">
              <p className="text-sm font-semibold text-gray-600 uppercase tracking-wide px-4 mb-3">Quick Book</p>
              <div className="grid grid-cols-1 gap-2 px-4">
                {affiliateLinks.map((link) => {
                  const IconComponent = link.icon;
                  const neutral = isNeutralPartner(link.name);
                  return (
                    <a
                      key={link.name}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackAffiliateClick(link.name, link.url, { location: 'Header-Mobile' })}
                      className={`${neutral ? 'bg-gray-100 text-gray-900 border border-gray-300' : `${link.color} text-white`} px-4 py-3 rounded-lg text-sm font-medium flex items-center space-x-3 transition-all duration-200 hover:opacity-90`}
                    >
                      <IconComponent size={18} />
                      <span>Book with {link.name}</span>
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header; 